import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input, Select } from "../components/ui";
import BrandFooter, { useBranding } from "../components/BrandFooter";
import { Headset, FlaskConical } from "lucide-react";
import { tp } from "../i18n";

export default function SupportLogin() {
  const { loginSupport, lang } = useApp();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [channel, setChannel] = useState(localStorage.getItem("suite_mfa_channel") || "email");
  const [remember, setRemember] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [info, setInfo] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const branding = useBranding();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/support-login", { username: username.trim(), password, channel, device_token: localStorage.getItem("suite_device_token") || undefined });
      localStorage.setItem("suite_mfa_channel", channel);
      if (data.mfa_required) {
        setMfa(data);
        setInfo(`We sent a 6-digit code to ${data.target} (${data.channel === "sms" ? "SMS" : "email"}).`);
      } else {
        if (data.device_token) localStorage.setItem("suite_device_token", data.device_token);
        loginSupport(data.token, data.user, data.session_id); nav("/app");
      }
    } catch (e2) { setErr(apiErr(e2)); }
    setBusy(false);
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/support-mfa/verify", { challenge_id: mfa.challenge_id, code: code.trim(), remember_device: remember, known_device: localStorage.getItem("suite_known_device") || undefined });
      if (data.device_token) localStorage.setItem("suite_device_token", data.device_token);
      if (data.known_device) localStorage.setItem("suite_known_device", data.known_device);
      loginSupport(data.token, data.user, data.session_id); nav("/app");
    } catch (e2) { setErr(apiErr(e2)); }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen place-items-center p-6" style={{ background: "var(--app-bg)" }}>
      <div className="w-full max-w-md glass rounded-2xl p-8 fadeup" data-testid="support-login-card">
        <div className="mb-6 flex items-center gap-3">
          <img src="/brand-logo.png" alt="Full Option Family Office" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-head text-lg font-bold tracking-tight" style={{ color: "var(--logo)" }} data-testid="support-suite-name">{branding?.suite_name || "FULL OPTION FAMILY OFFICE"}</div>
            <div className="font-head text-[11px] font-medium uppercase tracking-[0.18em] opacity-70">{tp("Support Sandbox", lang)}</div>
          </div>
        </div>
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/10 px-3 py-2 text-sm">
          <FlaskConical size={16} className="text-[var(--primary)]" />
          <span className="opacity-80">{tp("Technical-support training environment. Demo data only — purchaser data is never affected.", lang)}</span>
        </div>
        {mfa ? (
          <form onSubmit={verifyMfa} className="space-y-4" data-testid="support-mfa-form">
            <h1 className="mb-1 font-head text-2xl flex items-center gap-2"><Headset size={22} /> {tp("Verify it's you", lang)}</h1>
            {info && <p className="text-sm opacity-70" data-testid="support-mfa-info">{info}</p>}
            {mfa.debug_otp && <p className="text-xs text-[var(--primary)]" data-testid="support-mfa-debug-otp">Dev code: {mfa.debug_otp}</p>}
            <Input label={tp("6-digit code", lang)} value={code} inputMode="numeric" maxLength={6} onChange={(e) => setCode(e.target.value)} data-testid="support-mfa-code" autoFocus />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="support-mfa-remember" />
              {tp("Trust this device for 30 days", lang)}
            </label>
            {err && <p className="text-sm text-[#DA1E28]" data-testid="support-mfa-error">{err}</p>}
            <Button type="submit" disabled={busy || code.length < 6} className="w-full" data-testid="support-mfa-verify">
              {busy ? tp("Verifying…", lang) : tp("Verify & enter sandbox", lang)}
            </Button>
            <button type="button" onClick={() => { setMfa(null); setCode(""); setErr(""); }} className="w-full text-sm opacity-70 hover:opacity-100" data-testid="support-mfa-back">{tp("Back to sign in", lang)}</button>
          </form>
        ) : (
        <>
        <h1 className="mb-1 font-head text-2xl flex items-center gap-2"><Headset size={22} /> {tp("Support sign in", lang)}</h1>
        <p className="mb-6 text-sm opacity-60">{tp("Use the username & password assigned by your vendor.", lang)}</p>
        <form onSubmit={submit} className="space-y-4">
          <Input label={tp("Username", lang)} value={username} onChange={(e) => setUsername(e.target.value)} data-testid="support-username" autoFocus />
          <Input label={tp("Password", lang)} type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="support-password" />
          <Select label={tp("Verification method (MFA)", lang)} value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="support-mfa-channel">
            <option value="email">{tp("Email me a code", lang)}</option>
            <option value="sms">{tp("Text me a code (SMS)", lang)}</option>
          </Select>
          {err && <p className="text-sm text-[#DA1E28]" data-testid="support-login-error">{err}</p>}
          <Button type="submit" disabled={busy || !username || !password} className="w-full" data-testid="support-login-submit">
            {busy ? tp("Signing in…", lang) : tp("Continue", lang)}
          </Button>
        </form>
        </>
        )}
        <div className="mt-6 text-center text-xs opacity-50">
          <Link to="/login" className="hover:underline">{tp("Vendor / purchaser sign in →", lang)}</Link>
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
